import type { TFunction } from 'i18next';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  getMetadataString,
  getMetadataStringArray,
} from '../helpers/purchase-order-activity-metadata';
import type { PurchaseOrderActivityItem } from '../types/purchase-order-activity-item';
import { PurchaseOrderActivityDocumentBadge } from './purchase-order-activity-document-badge';

type PurchaseOrderActivityDocumentSentProps = {
  activity: PurchaseOrderActivityItem;
  documentUrlByName: Record<string, string>;
  t: TFunction<'purchaseOrder'>;
};

export function PurchaseOrderActivityDocumentSent({
  activity,
  documentUrlByName,
  t,
}: PurchaseOrderActivityDocumentSentProps) {
  const [isMessageOpen, setIsMessageOpen] = useState(false);
  const documentName = getMetadataString(activity.metadata, 'documentName');
  const documentUrl = documentName ? documentUrlByName[documentName] : null;
  const documentTypeCode = getMetadataString(
    activity.metadata,
    'documentTypeCode',
  );
  const recipients = getMetadataStringArray(activity.metadata, 'recipients');
  const subject = getMetadataString(activity.metadata, 'subject');
  const message = getMetadataString(activity.metadata, 'message');

  return (
    <>
      <span className='font-semibold text-foreground'>
        {t('activity.document.sent')}
      </span>
      {documentName ? (
        documentUrl ? (
          <a
            className='font-semibold text-primary underline underline-offset-2'
            href={documentUrl}
            rel='noopener noreferrer'
            target='_blank'
          >
            {documentName}
          </a>
        ) : (
          <span className='font-semibold text-primary underline underline-offset-2'>
            {documentName}
          </span>
        )
      ) : null}
      {documentTypeCode ? (
        <PurchaseOrderActivityDocumentBadge
          documentTypeCode={documentTypeCode}
        />
      ) : null}
      {message ? (
        <button
          className='font-medium text-foreground underline underline-offset-2'
          onClick={() => setIsMessageOpen(true)}
          type='button'
        >
          {t('activity.document.viewSentMessage')}
        </button>
      ) : null}
      {recipients.length > 0 ? (
        <span className='text-muted-foreground'>
          {t('activity.document.sentTo', { recipients: recipients.join(', ') })}
        </span>
      ) : null}
      {message ? (
        <Dialog onOpenChange={setIsMessageOpen} open={isMessageOpen}>
          <DialogContent className='sm:max-w-2xl'>
            <DialogHeader>
              <DialogTitle>
                {subject ?? t('activity.document.messageDialogTitle')}
              </DialogTitle>
            </DialogHeader>
            <pre className='max-h-[60vh] overflow-auto whitespace-pre-wrap font-sans text-foreground text-sm leading-6'>
              {message}
            </pre>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}
